import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Copy,
  Terminal,
  Code2,
  Zap,
  Link2,
  ShieldCheck,
  CheckCircle2,
  AlertTriangle,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { http } from "@/lib/api";
import DevDocsShell from "@/components/DevDocsShell";

const base = typeof window !== "undefined" ? window.location.origin : "";

const SNIPPETS = {
  curl: `curl -X POST "${base}/api/v1/leads/enrich?mode=sync" \\
  -H "Authorization: Bearer liq_live_YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "lead": {
      "name": "Jane Doe",
      "title": "VP of Marketing",
      "email": "jane@acme.com",
      "company": "Acme Corp",
      "domain": "acme.com"
    }
  }'`,
  python: `import requests

resp = requests.post(
    "${base}/api/v1/leads/enrich",
    headers={"Authorization": "Bearer liq_live_YOUR_KEY"},
    json={
        "lead": {
            "name": "Jane Doe",
            "title": "VP of Marketing",
            "email": "jane@acme.com",
            "company": "Acme Corp",
            "domain": "acme.com"
        },
        "callback_url": "https://yourapp.com/webhooks/leadiq"
    },
    timeout=30,
)
print(resp.json())  # {"job_id": "...", "status": "queued"}`,
  node: `const res = await fetch(
  "${base}/api/v1/leads/enrich",
  {
    method: "POST",
    headers: {
      "Authorization": "Bearer liq_live_YOUR_KEY",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      lead: {
        name: "Jane Doe",
        title: "VP of Marketing",
        email: "jane@acme.com",
        company: "Acme Corp",
        domain: "acme.com"
      },
      callback_url: "https://yourapp.com/webhooks/leadiq"
    })
  }
);
console.log(await res.json());`,
};

export default function Developers() {
  const [tab, setTab] = useState("curl");
  const [eligibility, setEligibility] = useState(null);

  useEffect(() => {
    // Only query eligibility if the user is logged in; endpoint 401s otherwise.
    http
      .get("/api-keys/eligibility")
      .then(({ data }) => setEligibility(data))
      .catch(() => setEligibility(null));
  }, []);

  const copy = (val) => navigator.clipboard.writeText(val).then(() => toast.success("Copied."));

  return (
    <DevDocsShell>
      <div data-testid="developers-page">
        <div className="mb-8">
          <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">
            Developer docs
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-ink-900 mt-2 tracking-tight">
            LeadIQ, headless.
          </h1>
          <p className="text-ink-500 text-base mt-3 max-w-2xl">
            A versioned REST API for lead enrichment, email discovery, and mobile lookups.
            Same quota and credits as your dashboard — no new SKUs, no second seat.
          </p>
        </div>

        {/* Quick-start cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          <StepCard
            num="1"
            icon={<ShieldCheck className="w-4 h-4" />}
            title="Create a key"
            body="Settings → API → Create key. Copy the secret once; it won't be shown again."
            cta={eligibility?.allowed ? "Open settings" : "Upgrade"}
            to="/settings/api"
          />
          <StepCard
            num="2"
            icon={<Terminal className="w-4 h-4" />}
            title="Make your first call"
            body="Hit /api/v1/leads/enrich with any lead and get a job_id back in under a second."
            cta="See example"
            anchor="first-call"
          />
          <StepCard
            num="3"
            icon={<Link2 className="w-4 h-4" />}
            title="Wire the webhook"
            body="Pass callback_url on every request; we POST the enriched payload when it's ready."
            cta="Webhook format"
            anchor="webhooks"
          />
        </div>

        {/* Base URL + Auth */}
        <section className="rounded-sm border border-ink-100 bg-ink-50 p-6 mb-8">
          <h2 className="font-display text-xl font-bold text-ink-900">Authentication</h2>
          <p className="text-ink-500 text-sm mt-2">
            Every call needs a Bearer API key in the <code className="font-mono text-ink-900">Authorization</code> header.
            Keys are scoped to a user and inherit that user's tier limits.
          </p>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            <KV label="Base URL" value={`${base}/api/v1`} onCopy={copy} />
            <KV label="Header" value="Authorization: Bearer liq_live_…" onCopy={copy} mono />
          </div>
          <div className="mt-4 text-xs text-ink-500 flex items-start gap-2">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
            <span>
              Tier gate: Growth, Scale, and Enterprise plans. Starter and Free see an upgrade prompt
              on the Settings → API tab.
            </span>
          </div>
        </section>

        {/* First call */}
        <section id="first-call" className="rounded-sm border border-ink-100 bg-ink-50 p-6 mb-8">
          <h2 className="font-display text-xl font-bold text-ink-900 flex items-center gap-2">
            <Zap className="w-4 h-4 text-brand-700" /> Enrich your first lead
          </h2>
          <p className="text-ink-500 text-sm mt-2">
            By default this is async — we return a <code className="font-mono text-ink-900">job_id</code> in
            &lt;1&nbsp;s and POST the enriched payload to your <code className="font-mono text-ink-900">callback_url</code> when
            ready. For scripts, pass <code className="font-mono text-ink-900">?mode=sync</code> to block up to ~90&nbsp;s and
            get the lead inline.
          </p>
          <div className="mt-4 flex items-center gap-1 border-b border-ink-100">
            {["curl", "python", "node"].map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`text-xs uppercase tracking-widestx font-mono px-3 py-2 border-b-2 transition-colors ${
                  tab === t
                    ? "border-ink-900 text-ink-900"
                    : "border-transparent text-ink-400 hover:text-ink-700"
                }`}
                data-testid={`dev-tab-${t}`}
              >
                {t}
              </button>
            ))}
            <div className="ml-auto">
              <button
                onClick={() => copy(SNIPPETS[tab])}
                className="inline-flex items-center gap-1 text-xs text-ink-500 hover:text-ink-900"
              >
                <Copy className="w-3 h-3" /> Copy
              </button>
            </div>
          </div>
          <pre className="mt-3 bg-ink-900 text-ink-50 rounded-sm p-4 text-xs leading-relaxed overflow-x-auto font-mono">
            {SNIPPETS[tab]}
          </pre>
          <div className="mt-4 text-sm">
            <Link
              to="/developers/reference"
              className="inline-flex items-center gap-1 text-brand-700 hover:text-brand-900 font-medium"
            >
              Open the full API reference <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </section>

        {/* Webhooks */}
        <section id="webhooks" className="rounded-sm border border-ink-100 bg-ink-50 p-6 mb-8">
          <h2 className="font-display text-xl font-bold text-ink-900 flex items-center gap-2">
            <Link2 className="w-4 h-4 text-brand-700" /> Webhooks
          </h2>
          <p className="text-ink-500 text-sm mt-2">
            Pass <code className="font-mono text-ink-900">callback_url</code> in your enrich
            request body and we'll POST the enriched payload to that URL when the job finishes.
            Delivery happens once in Phase 1; signed payloads and retries land in Phase 2.
          </p>
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 mb-1">
              Payload shape
            </div>
            <pre className="bg-ink-900 text-ink-50 rounded-sm p-4 text-xs leading-relaxed overflow-x-auto font-mono">
{`{
  "job_id": "c8fcba2b-335a-4652-aeec-25461bf76c9c",
  "kind": "lead.enrich",
  "status": "done",
  "created_at": "2026-02-14T10:12:00Z",
  "finished_at": "2026-02-14T10:12:38Z",
  "result": {
    "id": "68f3ab...",
    "input": { "company": "Acme Corp", "domain": "acme.com", ... },
    "enrichment": {
      "company_overview": { ... },
      "tech_stack": { ... },
      "intent_signals": { ... },
      "buying_committee": [ ... ],
      "opportunity_score": 72,
      ...
    },
    "created_at": "2026-02-14T10:12:38Z"
  },
  "error": null
}`}
            </pre>
          </div>
          <p className="mt-4 text-sm text-ink-500">
            For a field-by-field explanation of what each enrichment key means, see the{" "}
            <Link to="/developers/glossary" className="text-brand-700 hover:underline font-medium">
              enriched-field glossary
            </Link>
            .
          </p>
        </section>

        {/* Limits + 429 variants */}
        <section className="rounded-sm border border-ink-100 bg-ink-50 p-6 mb-8">
          <h2 className="font-display text-xl font-bold text-ink-900">Rate limits</h2>
          <ul className="text-ink-700 text-sm mt-3 space-y-1.5">
            <li>• 60 requests / minute per key on sync endpoints</li>
            <li>• 600 requests / minute per key on async job creation</li>
            <li>• Monthly enrichment quota, email credits, and mobile credits are shared with the dashboard</li>
            <li>• Over-limit calls return <code className="font-mono text-ink-900">429</code> with a <code className="font-mono text-ink-900">Retry-After</code> header</li>
            <li>• Global ceiling of <b>5 concurrent in-flight enrichments</b> across the platform. If you hit it, you get <code className="font-mono text-ink-900">429 Retry-After</code> — space your calls out and they'll go through.</li>
          </ul>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
            <ErrorCard
              title="Per-key 429"
              desc="You exceeded 60 req/min (sync) or 600 req/min (async) on this specific key. Back off that key only — other keys are unaffected."
              detail='{"detail":"Rate limit exceeded (60 req/min). Retry in 42s."}'
            />
            <ErrorCard
              title="upstream_full 429"
              desc="The platform-wide ceiling of 5 concurrent enrichments is saturated. Short back-off (~2 s) is enough — the next freed slot is yours."
              detail='{"detail":"upstream_full — the platform is at its concurrent enrichment ceiling. Retry in a few seconds."}'
            />
          </div>
        </section>

        {/* Where to go next */}
        <section className="rounded-sm border border-ink-100 bg-ink-50 p-6">
          <h2 className="font-display text-xl font-bold text-ink-900">Where to next</h2>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            <NextLink
              to="/developers/reference"
              title="API Reference"
              desc="Every endpoint, request body, response schema, and error code."
            />
            <NextLink
              to="/developers/glossary"
              title="Enriched-field glossary"
              desc="What each key in the enriched payload means, and when it's null."
            />
            <NextLink
              to="/developers/recipes/hubspot"
              title="Integration recipes"
              desc="HubSpot, Pipedrive, Salesforce, Zapier — copy-paste and ship."
            />
          </div>
        </section>
      </div>
    </DevDocsShell>
  );
}

function ErrorCard({ title, desc, detail }) {
  return (
    <div className="rounded-sm border border-amber-200 bg-white p-4">
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-600" />
        <div className="font-mono text-xs uppercase tracking-widestx text-amber-900 font-semibold">
          {title}
        </div>
      </div>
      <p className="text-sm text-ink-700 mt-2">{desc}</p>
      <pre className="mt-3 bg-ink-900 text-ink-50 rounded-sm px-2 py-1.5 text-[11px] font-mono whitespace-pre-wrap break-all leading-relaxed">
        {detail}
      </pre>
    </div>
  );
}

function NextLink({ to, title, desc }) {
  return (
    <Link
      to={to}
      className="block rounded-sm border border-ink-100 bg-white p-4 hover:border-brand-200 hover:shadow-sm transition"
    >
      <div className="flex items-center gap-2 font-semibold text-ink-900">
        {title}
        <ArrowRight className="w-3.5 h-3.5 text-brand-700" />
      </div>
      <p className="text-xs text-ink-500 mt-1">{desc}</p>
    </Link>
  );
}

function StepCard({ num, title, body, icon, cta, to, anchor }) {
  const Inner = (
    <>
      <div className="flex items-center gap-2">
        <span className="font-mono text-[10px] uppercase tracking-widestx text-ink-400">
          Step {num}
        </span>
        <span className="text-brand-700">{icon}</span>
      </div>
      <h3 className="font-display text-lg font-bold text-ink-900 mt-2">{title}</h3>
      <p className="text-ink-500 text-sm mt-1 min-h-[3.5rem]">{body}</p>
      <div className="mt-3 text-brand-700 text-xs font-semibold uppercase tracking-widestx font-mono inline-flex items-center gap-1">
        {cta} →
      </div>
    </>
  );
  const cls =
    "block rounded-sm border border-ink-100 bg-ink-50 p-5 hover:border-brand-200 hover:shadow-sm transition";
  if (to) {
    return (
      <Link to={to} className={cls}>
        {Inner}
      </Link>
    );
  }
  return (
    <a href={`#${anchor}`} className={cls}>
      {Inner}
    </a>
  );
}

function KV({ label, value, onCopy, mono }) {
  return (
    <div className="rounded-sm border border-ink-100 bg-white p-3 flex items-center gap-3">
      <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-500 w-20 shrink-0">
        {label}
      </div>
      <div className={`flex-1 text-ink-900 truncate ${mono ? "font-mono text-sm" : ""}`}>
        {value}
      </div>
      <button
        onClick={() => onCopy(value)}
        className="p-1.5 text-ink-500 hover:text-ink-900 rounded-sm hover:bg-ink-50"
        title="Copy"
      >
        <Copy className="w-4 h-4" />
      </button>
    </div>
  );
}
